/**
 * VendorPortalProductMediaService
 * Service for vendor product media management.
 * Route: api/vendor-portal/products/{productId}/media
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalProductMediaService
 * Handles vendor product media operations for the vendor portal.
 * Maps to /api/vendor-portal/products/{productId}/media endpoint
 */
export class VendorPortalProductMediaService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalProductMediaService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalProductMediaService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all media for a specific product
     * GET /api/vendor-portal/products/{productId}/media
     */
    async getMedia(productId) {
        const response = await this.getMediaRaw(productId);
        return response.data ?? [];
    }
    /**
     * Get all media (raw response)
     */
    getMediaRaw(productId) {
        return this.get(`${productId}/media`);
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific media item by ID
     * GET /api/vendor-portal/products/{productId}/media/{mediaId}
     */
    async getMediaItem(productId, mediaId) {
        const response = await this.getMediaItemRaw(productId, mediaId);
        if (!response.data) {
            throw new Error(`Media with ID ${mediaId} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific media item (raw response)
     */
    getMediaItemRaw(productId, mediaId) {
        return this.get(`${productId}/media/${mediaId}`);
    }
    // ==================== CREATE ====================
    /**
     * Create a new media item for a product
     * POST /api/vendor-portal/products/{productId}/media
     */
    async createMedia(productId, data) {
        const response = await this.createMediaRaw(productId, data);
        if (!response.data) {
            throw new Error('Failed to create media');
        }
        return response.data;
    }
    /**
     * Create a new media item (raw response)
     */
    createMediaRaw(productId, data) {
        return this.post(`${productId}/media`, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing media item
     * PUT /api/vendor-portal/products/{productId}/media/{mediaId}
     */
    async updateMedia(productId, mediaId, data) {
        const response = await this.updateMediaRaw(productId, mediaId, data);
        if (!response.data) {
            throw new Error(`Failed to update media with ID ${mediaId}`);
        }
        return response.data;
    }
    /**
     * Update an existing media item (raw response)
     */
    updateMediaRaw(productId, mediaId, data) {
        return this.put(`${productId}/media/${mediaId}`, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a media item
     * DELETE /api/vendor-portal/products/{productId}/media/{mediaId}
     */
    async deleteMedia(productId, mediaId) {
        const response = await this.deleteMediaRaw(productId, mediaId);
        return response.success;
    }
    /**
     * Delete a media item (raw response)
     */
    deleteMediaRaw(productId, mediaId) {
        return this.delete(`${productId}/media/${mediaId}`);
    }
    // ==================== SET PRIMARY ====================
    /**
     * Set a media item as the primary image
     * POST /api/vendor-portal/products/{productId}/media/{mediaId}/set-primary
     */
    async setPrimary(productId, mediaId) {
        const response = await this.setPrimaryRaw(productId, mediaId);
        if (!response.data) {
            throw new Error(`Failed to set media ${mediaId} as primary`);
        }
        return response.data;
    }
    /**
     * Set primary media (raw response)
     */
    setPrimaryRaw(productId, mediaId) {
        return this.post(`${productId}/media/${mediaId}/set-primary`, {});
    }
}
VendorPortalProductMediaService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorPortalProductMediaService.BASE_PATH = 'api/vendor-portal/products';
//# sourceMappingURL=VendorPortalProductMediaService.js.map